import queryString from "query-string";
import { checkIsExtension } from "./helpers";
export const CORE_BEARER_TOKEN = "CORE_BEARER_TOKEN";
export const CURRENT_ACCOUNT_ID = "CURRENT_ACCOUNT_ID";
/**
 * Sets the bearer token to storage.
 *
 * @param {string} token - The bearer token to be set.
 */
export const setBearerTokenToStorage = (token) => {
    if (checkIsExtension()) {
        chrome.storage.local.set({ [CORE_BEARER_TOKEN]: token });
    }
    else {
        localStorage.setItem(CORE_BEARER_TOKEN, token);
    }
};
/**
 * Retrieves the bearer token from storage.
 * If the application is running as an extension, it retrieves the token from the Chrome local storage using a Promise.
 * If the application is running in a regular browser environment, it retrieves the token from the local storage directly.
 *
 * @returns {Promise<string | null> | string | null} - The bearer token if it exists, or null if it doesn't.
 */
export const getBearerTokenFromStorage = () => {
    if (checkIsExtension()) {
        return new Promise((resolve) => {
            try {
                chrome?.storage?.local?.get([CORE_BEARER_TOKEN], (value) => {
                    if (value[CORE_BEARER_TOKEN]) {
                        resolve(value[CORE_BEARER_TOKEN]);
                    }
                    else {
                        resolve(null);
                    }
                });
            }
            catch {
                resolve(null);
            }
        });
    }
    else {
        return localStorage.getItem(CORE_BEARER_TOKEN);
    }
};
/**
 * Set bearer token for a specific account.
 *
 * @async
 * @param {string} accountId - The ID of the account.
 * @param {string} token - The bearer token to set.
 * @returns {Promise<void>} - A promise that resolves when the token is set successfully.
 */
export const setBearerToken = async (accountId, token) => {
    const data = JSON.parse((await getBearerTokenFromStorage()) || "{}");
    setBearerTokenToStorage(JSON.stringify({ ...data, [accountId]: token }));
};
/**
 * Retrieves the bearer token associated with the specified account ID.
 *
 * @param {string} accountId - The ID of the account.
 *
 * @returns {Promise<string | null>} - A Promise that resolves to the bearer token, or null if it is not found.
 */
export const getBearerToken = async (accountId) => {
    return accountId
        ? JSON.parse((await getBearerTokenFromStorage()) || "{}")[accountId] || null
        : null;
};
/**
 * Retrieves the authorization header for the given account.
 *
 * @param {string} accountId - The ID of the account.
 * @returns {Promise<{ Authorization?: string }>} A promise that resolves to the authorization header.
 */
export const getAuthorizationHeader = async (accountId) => {
    const token = await getBearerToken(accountId);
    return token ? { Authorization: `Bearer ${token}` } : {};
};
/**
 * Sets the current account ID.
 * If the application is running as a Chrome extension, the account ID is stored in the local storage.
 * Otherwise, it is stored in the session storage.
 *
 * @param {string} accountId - The ID of the account to set as the current account.
 */
export const setCurrentAccountId = (accountId) => {
    if (checkIsExtension()) {
        chrome.storage.local.set({ [CURRENT_ACCOUNT_ID]: Number(accountId) });
    }
    else {
        sessionStorage.setItem(CURRENT_ACCOUNT_ID, accountId);
    }
};
/**
 * Retrieves the cross-platform account ID.
 *
 * If the current environment is an extension, it retrieves the account ID from the local storage.
 * If the current environment is not an extension, it retrieves the account ID from the query string parameter "accountId" in the URL,
 * or from the session storage, or from the global "window" object.
 *
 * @returns {Promise<string | null> | string|null} The cross-platform account ID or null if it is not available.
 */
export const getCrossPlatformAccountId = () => {
    if (checkIsExtension()) {
        return new Promise((resolve) => {
            try {
                chrome?.storage?.local?.get([CURRENT_ACCOUNT_ID], (value) => {
                    if (value[CURRENT_ACCOUNT_ID]) {
                        resolve(value[CURRENT_ACCOUNT_ID]);
                    }
                    else {
                        resolve(null);
                    }
                });
            }
            catch {
                resolve(null);
            }
        });
    }
    else {
        return (queryString.parse(window.location.search)["accountId"] ||
            sessionStorage.getItem(CURRENT_ACCOUNT_ID) ||
            window.accountId);
    }
};
/**
 * Retrieves the current account ID from the application state or URL query parameters.
 *
 * @returns {string | null} The current account ID, or null if not found.
 */
export const getCurrentAccountId = () => {
    return (queryString.parse(window.location.search)["accountId"] ||
        sessionStorage.getItem(CURRENT_ACCOUNT_ID) ||
        String(window.accountId || ""));
};
/**
 * Returns the authorization header for the current session.
 *
 * @async
 * @function getSessionAuthorizationHeader
 * @returns {Promise<{ Authorization?: string; }>} A promise that resolves to an object containing the authorization header.
 */
export const getSessionAuthorizationHeader = async () => {
    const currentAccountId = (await getCrossPlatformAccountId()) || null;
    return currentAccountId ? await getAuthorizationHeader(currentAccountId) : {};
};
